import React, { useState } from 'react';
import { ArrowUpRight, X } from 'lucide-react';
import { portfolioCompanies, companyLogos } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const GalaxyMap = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [headerRef, headerVisible] = useScrollAnimation(0.1);

  const getCompanyLogo = (companyId) => {
    const logoMap = {
      lobbylogix: companyLogos.lobbylogix,
      shielddeck: companyLogos.shielddeck,
      statziq: companyLogos.statziq,
      aligned: companyLogos.aligned,
      averitas: companyLogos.averitas,
    };
    return logoMap[companyId];
  };

  return (
    <section id="systems" className="relative py-32 lg:py-40">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headerRef}
          className={`max-w-2xl mb-20 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-purple-400 text-sm font-medium tracking-widest uppercase mb-4">Portfolio</p>
          <h2 className="font-display text-4xl lg:text-5xl text-white mb-6">
            Our Operating <span className="text-gradient">Systems</span>
          </h2>
          <p className="text-white/50 text-lg">
            Purpose-built platforms engineered for specific business challenges.
          </p>
        </div>

        {/* Companies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioCompanies.map((company, index) => (
            <CompanyCard
              key={company.id}
              company={company}
              logo={getCompanyLogo(company.id)}
              index={index}
              onClick={() => setSelectedCompany(company)}
            />
          ))}
        </div>

        {/* Modal */}
        <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
          <DialogContent className="bg-[#0c0c10] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{selectedCompany?.name}</DialogTitle>
              <DialogDescription className="text-white/50">{selectedCompany?.tagline}</DialogDescription>
            </DialogHeader>
            {selectedCompany && (
              <div className="mt-4 space-y-6">
                <p className="text-white/60 leading-relaxed">{selectedCompany.description}</p>
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Capabilities</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCompany.capabilities.map((cap) => (
                      <span key={cap} className="px-3 py-1 text-xs border border-white/10 text-white/60">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
                <a
                  href={selectedCompany.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-all"
                >
                  Visit Site <ArrowUpRight size={16} />
                </a>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

const CompanyCard = ({ company, logo, index, onClick }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  // Some logos have internal whitespace, so we need to offset them visually
  const getLogoStyle = (companyId) => {
    // These values counteract the transparent padding baked into the image files
    // and adjust sizes for consistency
    const offsets = {
      lobbylogix: { transform: 'scale(0.7)', transformOrigin: 'left center' },
      statziq: { marginLeft: '-2.5rem', transform: 'scale(1.4)', transformOrigin: 'left center' },
      shielddeck: { marginLeft: '-1.5rem' },
    };
    return offsets[companyId] || {};
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`group relative p-8 text-left bg-white/[0.02] border border-white/5 hover:border-purple-500/30 hover:bg-white/[0.04] transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Logo Container */}
      <div className="h-24 mb-6 flex items-center overflow-visible">
        {logo ? (
          <img 
            src={logo} 
            alt={company.name} 
            className="h-full w-auto max-w-[220px] object-contain"
            style={{ objectPosition: 'left center', ...getLogoStyle(company.id) }}
          />
        ) : (
          <div
            className="w-16 h-16 rounded-lg flex items-center justify-center text-lg font-bold"
            style={{ background: `${company.color}20`, color: company.color }}
          >
            {company.name.substring(0, 2)}
          </div>
        )}
      </div>

      {/* Content */}
      <h3 className="text-lg text-white font-medium mb-2 group-hover:text-purple-400 transition-colors">
        {company.name}
      </h3>
      <p className="text-white/40 text-sm line-clamp-2">{company.tagline}</p>

      {/* Arrow */}
      <ArrowUpRight
        size={18}
        className="absolute top-6 right-6 text-white/20 group-hover:text-purple-400 transition-all group-hover:translate-x-1 group-hover:-translate-y-1"
      />
    </button>
  );
};

export default GalaxyMap;
